import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { Icon } from "./icons";
import { search, type Hit } from "../lib/search";
import type { NavItem } from "./Layout";
import { useOrg } from "../lib/session";

// The sidebar's search: a field that opens a small panel where people type
// what they want to do and jump straight there (Ctrl/⌘ K or "/" anywhere).

const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);

/** scrollToHeading finds a section heading on the page that just opened and
 *  brings it into view with a short highlight. Pages load their data first,
 *  so it looks for up to three seconds. */
function scrollToHeading(text: string) {
  const want = text.toLowerCase();
  const started = Date.now();
  const look = () => {
    const hs = Array.from(document.querySelectorAll<HTMLElement>("main h2, main h3"));
    const t = (h: HTMLElement) => (h.textContent ?? "").trim().toLowerCase();
    const h = hs.find((x) => t(x) === want) ?? hs.find((x) => t(x).startsWith(want)) ?? hs.find((x) => t(x).includes(want));
    if (h) {
      const box = (h.closest("section, .card") as HTMLElement | null) ?? h;
      box.scrollIntoView({ behavior: "smooth", block: "start" });
      box.classList.remove("search-flash");
      void box.offsetWidth;
      box.classList.add("search-flash");
      window.setTimeout(() => box.classList.remove("search-flash"), 1800);
      return;
    }
    if (Date.now() - started < 3000) window.setTimeout(look, 120);
  };
  window.setTimeout(look, 60);
}

/** base is the app path of the company (/o/<id>), not the API path. */
export function SidebarSearch({ items, base, collapsed }: { items: NavItem[]; base: string; collapsed: boolean }) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const typing = e.target instanceof HTMLElement && (e.target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(e.target.tagName));
      if ((e.key === "k" || e.key === "K") && (e.metaKey || e.ctrlKey)) { e.preventDefault(); setOpen(true); }
      else if (e.key === "/" && !typing && !e.metaKey && !e.ctrlKey && !e.altKey) { e.preventDefault(); setOpen(true); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  const hint = isMac ? "⌘K" : "Ctrl K";
  return (
    <>
      <button type="button" className="side-search" onClick={() => setOpen(true)} title={collapsed ? `Search (${hint})` : undefined} aria-label="Search SVENEX" aria-haspopup="dialog">
        <span className="nav-ico"><Icon name="search" /></span>
        <span className="nav-label">Search</span>
        <kbd className="nav-label">{hint}</kbd>
      </button>
      {/* On <body>, so the sidebar's own layering can't cover or clip it. */}
      {open && createPortal(<SearchPanel items={items} base={base} onClose={() => setOpen(false)} />, document.body)}
    </>
  );
}

function SearchPanel({ items, base, onClose }: { items: NavItem[]; base: string; onClose: () => void }) {
  const navigate = useNavigate();
  const { allows } = useOrg();
  const [q, setQ] = useState("");
  const [at, setAt] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLUListElement>(null);
  const allowed = useMemo(() => new Set(items.map((i) => i.key)), [items]);
  const labels = useMemo(() => new Map(items.map((i) => [i.key, i.label])), [items]);
  // Before anything is typed, the pages themselves (by their own names).
  const hits: Hit[] = useMemo(() => {
    if (q.trim()) return search(q, allowed, labels, 8, allows);
    return [];
  }, [q, allowed, labels, allows]);
  useEffect(() => { input.current?.focus(); }, []);
  useEffect(() => { setAt(0); }, [q]);
  useEffect(() => {
    list.current?.querySelector<HTMLElement>(`[data-i="${at}"]`)?.scrollIntoView({ block: "nearest" });
  }, [at]);
  const title = (h: Hit) => {
    const f = h.feature;
    // A whole page shows under the company's own name for it ("Patients").
    const own = labels.get(f.key);
    return own && f.page ? own : f.title;
  };
  const where = (h: Hit) => {
    const page = labels.get(h.feature.key) ?? "";
    const t = title(h);
    return t === page ? "" : page;
  };
  const go = (h: Hit) => {
    onClose();
    navigate(base + h.feature.path);
    if (h.feature.find) scrollToHeading(h.feature.find);
  };
  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") { e.preventDefault(); onClose(); }
    else if (e.key === "ArrowDown") { e.preventDefault(); setAt((a) => Math.min(a + 1, Math.max(hits.length - 1, 0))); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setAt((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Enter" && hits[at]) { e.preventDefault(); go(hits[at]); }
  };
  return (
    <div className="modal-scrim search-scrim" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="search-panel" role="dialog" aria-modal="true" aria-label="Search SVENEX" onKeyDown={onKey}>
        <div className="search-field">
          <Icon name="search" />
          <input ref={input} value={q} onChange={(e) => setQ(e.target.value.slice(0, 120))} placeholder="What do you want to do? e.g. change password, add staff, opening hours"
            role="combobox" aria-expanded={hits.length > 0} aria-controls="search-results" aria-activedescendant={hits[at] ? `search-hit-${at}` : undefined}
            autoComplete="off" spellCheck={false} maxLength={120} />
          <kbd>Esc</kbd>
        </div>
        {q.trim() === "" ? (
          <p className="search-empty muted small">Type a page or something you want to do, in your own words. English or Arabic.</p>
        ) : hits.length === 0 ? (
          <p className="search-empty muted small">Nothing found for "{q.trim()}". Try other words, or ask the setup assistant.</p>
        ) : (
          <ul className="search-results" id="search-results" role="listbox" ref={list}>
            {hits.map((h, i) => (
              <li key={h.feature.title + h.feature.path} id={`search-hit-${i}`} data-i={i} role="option" aria-selected={i === at}
                className={i === at ? "on" : undefined} onMouseMove={() => setAt(i)} onClick={() => go(h)}>
                <span className="nav-ico"><Icon name={h.feature.key} /></span>
                <span className="grow">{title(h)}</span>
                {where(h) && <span className="muted small">{where(h)}</span>}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
